import React, { useState, useEffect, useRef } from "react";
import { sendContactMail } from "./mailService";

// ─────────────────────────────────────────────
// INLINE STYLES (replaces VKTemplate1.css)
// ─────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;600;800&family=Noto+Sans+Tamil:wght@400;600&display=swap');

  :root {
    --c1: #7C3AED;   /* violet */
    --c2: #EC4899;   /* pink */
    --c3: #F59E0B;   /* amber */
    --c4: #10B981;   /* emerald */
    --c5: #3B82F6;   /* blue */
    --c6: #EF4444;   /* red */
    --bg: #F0F4FF;
    --card-en: linear-gradient(135deg,#EDE9FE 0%,#FAF5FF 100%);
    --card-ta: linear-gradient(135deg,#FFF7ED 0%,#FFFBEB 100%);
    --radius-card: 20px;
    --shadow-sm:  0 2px 8px #7C3AED0d;
    --shadow-md:  0 4px 20px #7C3AED14;
    --shadow-lg:  0 8px 32px #7C3AED1f;
    --transition: all .2s cubic-bezier(.4,0,.2,1);
  }

  body { background: var(--bg); }

  /* smooth scroll */
  html { scroll-behavior: smooth; }

  .vk-root {
    font-family: 'Baloo 2', sans-serif;
    max-width: 1100px; margin: 0 auto; padding: 16px;
    /* subtle dot-grid background */
    background-image: radial-gradient(#7C3AED18 1px, transparent 1px);
    background-size: 28px 28px;
    background-color: var(--bg);
  }

  /* ── search bar ── */
  .vk-search {
    border: 2px solid #DDD6FE;
    border-radius: 50px;
    padding: 11px 20px;
    font-size: 14px;
    width: 100%;
    outline: none;
    transition: var(--transition);
    background: #fff;
    box-shadow: var(--shadow-sm);
    font-family: 'Baloo 2', sans-serif;
  }
  .vk-search:focus {
    border-color: var(--c1);
    box-shadow: 0 0 0 4px #7C3AED22, var(--shadow-md);
  }
  .vk-search::placeholder { color: #A78BFA; }

  @keyframes pulse { from { box-shadow: 0 0 0 0 #EC489966; } to { box-shadow: 0 0 0 10px transparent; } }

  /* ── chapter pills ── */
  .vk-chapter-pill {
    border-radius: 50px;
    font-size: 12px;
    font-weight: 700;
    padding: 5px 15px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all .18s cubic-bezier(.4,0,.2,1);
    font-family: 'Baloo 2', sans-serif;
  }
  .vk-chapter-pill:hover { opacity: .85; transform: translateY(-1px); }
  .vk-chapter-pill.active {
    color: #fff !important;
    transform: scale(1.06) translateY(-1px);
    box-shadow: 0 4px 14px #0004;
  }

  /* ── question card ── */
  .vk-card {
    border-radius: var(--radius-card);
    padding: 18px 18px 14px;
    margin-bottom: 14px;
    box-shadow: var(--shadow-md);
    transition: transform .2s cubic-bezier(.4,0,.2,1), box-shadow .2s;
    position: relative;
    overflow: hidden;
  }
  .vk-card:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-lg);
  }
  .vk-card::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0; width: 5px;
    border-radius: var(--radius-card) 0 0 var(--radius-card);
  }
  /* subtle shimmer strip at top of each card */
  .vk-card::after {
    content: '';
    position: absolute; left: 5px; top: 0; right: 0; height: 2px;
    border-radius: 0 var(--radius-card) 0 0;
    opacity: .35;
  }
  .vk-card-en { background: var(--card-en); }
  .vk-card-en::before { background: linear-gradient(180deg,#7C3AED,#A78BFA); }
  .vk-card-en::after  { background: linear-gradient(90deg,#7C3AED,#EC4899); }
  .vk-card-ta { background: var(--card-ta); }
  .vk-card-ta::before { background: linear-gradient(180deg,#F59E0B,#FCD34D); }
  .vk-card-ta::after  { background: linear-gradient(90deg,#F59E0B,#EC4899); }

  .vk-q-text {
    font-size: 14px; font-weight: 700; color: #1e1b4b;
    line-height: 1.5; margin-bottom: 6px;
  }
  .vk-a-text {
    font-size: 13px; color: #555; line-height: 1.7;
    border-left: 3px solid #EDE9FE; padding-left: 10px; margin-top: 4px;
  }

  .vk-icon-btn {
    background: rgba(124,58,237,.07); border: none; cursor: pointer;
    font-size: 17px; padding: 5px 7px;
    border-radius: 10px;
    transition: transform .15s, background .15s;
  }
  .vk-icon-btn:hover { transform: scale(1.18); background: rgba(124,58,237,.15); }

  /* ── chapter badge ── */
  .vk-badge {
    display: inline-block;
    background: linear-gradient(135deg,var(--c6),#FCA5A5);
    color: #fff;
    border-radius: 50px;
    font-size: 10px;
    font-weight: 800;
    padding: 3px 11px;
    letter-spacing: .3px;
    box-shadow: 0 2px 6px #EF444433;
  }
  .vk-updated {
    font-size: 9px; color: var(--c5); font-weight: 600;
    background: #EFF6FF; border-radius: 50px; padding: 2px 8px;
  }

  /* ── pagination ── */
  .vk-page-btn {
    border-radius: 50px !important;
    font-size: 12px !important;
    font-weight: 700;
    padding: 6px 16px !important;
    border: 2px solid var(--c1) !important;
    color: var(--c1) !important;
    background: #fff !important;
    transition: all .18s !important;
    box-shadow: var(--shadow-sm);
    font-family: 'Baloo 2', sans-serif;
  }
  .vk-page-btn.active, .vk-page-btn:hover:not(:disabled) {
    background: linear-gradient(135deg,var(--c1),#A78BFA) !important;
    color: #fff !important;
    border-color: transparent !important;
    box-shadow: 0 4px 14px #7C3AED44 !important;
    transform: translateY(-1px);
  }
  .vk-page-btn:disabled { opacity: .35; pointer-events: none; }

  /* ── inline nav toolbar ── */
  .vk-floating-grid {
    position: fixed;
    z-index: 999;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: rgba(255,255,255,0.85);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-radius: 14px;
    padding: 5px;
    box-shadow: 0 4px 20px #7C3AED22, 0 1px 4px #0002;
    border: 1px solid rgba(124,58,237,.12);
    cursor: grab;
    user-select: none;
    touch-action: none;
  }
  .vk-floating-grid:active { cursor: grabbing; }
  .vk-float-item {
    width: 22px; height: 22px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    font-size: 11px;
    color: #fff;
    transition: transform .15s, box-shadow .15s;
    box-shadow: 0 2px 6px #0002;
  }
  .vk-float-item:hover { transform: scale(1.22); box-shadow: 0 3px 10px #0003; }

  /* ── modals ── */
  .vk-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,.55);
    display: flex; align-items: center; justify-content: center;
    z-index: 2000;
  }
  .vk-modal-box {
    background: #fff;
    border-radius: 24px;
    padding: 28px;
    width: 92%; max-width: 560px;
    box-shadow: 0 24px 80px #7C3AED22, 0 8px 32px #0002;
    animation: popIn .28s cubic-bezier(.34,1.56,.64,1);
    border: 1px solid rgba(124,58,237,.08);
  }
  @keyframes popIn { from { transform: scale(.85); opacity:0; } to { transform: scale(1); opacity:1; } }

  /* ── play modal ── */
  .vk-read-progress { background: #E0E7FF; border-radius: 99px; overflow: hidden; height: 8px; }
  .vk-read-progress-bar {
    height: 100%;
    background: linear-gradient(90deg,var(--c1),var(--c2));
    border-radius: 99px;
    transition: width .4s ease;
  }
  .vk-round-controls { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
  .vk-round-btn {
    width: 50px; height: 50px; border-radius: 50%;
    border: none; font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    background: #EDE9FE; color: var(--c1);
    transition: all .18s cubic-bezier(.34,1.56,.64,1);
    box-shadow: 0 2px 8px #7C3AED1a;
  }
  .vk-round-btn:hover {
    background: linear-gradient(135deg,var(--c1),#A78BFA);
    color: #fff; transform: scale(1.12);
    box-shadow: 0 4px 16px #7C3AED44;
  }
  .vk-round-btn:disabled { opacity: .35; pointer-events: none; }
  .vk-pause { background: #FEF3C7; color: var(--c3); }
  .vk-resume { background: #D1FAE5; color: var(--c4); }
  .vk-stop { background: #FEE2E2; color: var(--c6); }

  /* ── dark mode ── */
  .vk-dark {
    background: #0C0818 !important;
    color: #E2D9FF !important;
    background-image: radial-gradient(#7C3AED0a 1px, transparent 1px) !important;
    background-size: 28px 28px !important;
    background-color: #0C0818 !important;
  }
  .vk-dark .vk-card { box-shadow: 0 4px 24px #00000033; }
  .vk-dark .vk-card-en {
    background: linear-gradient(135deg,#1A0E3D,#251552) !important;
    border-color: #3D2870;
  }
  .vk-dark .vk-card-ta {
    background: linear-gradient(135deg,#1E1409,#2A1E0F) !important;
    border-color: #4A3000;
  }
  .vk-dark .vk-card-en::after { opacity: .2; }
  .vk-dark .vk-card-ta::after { opacity: .2; }
  .vk-dark .vk-q-text { color: #C4B5FD !important; }
  .vk-dark .vk-a-text { color: #A5B4FC !important; border-left-color: #3D2870 !important; }
  .vk-dark .vk-search { background: #1A0E3D; color: #E2D9FF; border-color: #4C2D9E; }
  .vk-dark .vk-search::placeholder { color: #6D5AA8; }
  .vk-dark .vk-modal-box { background: #1A0E3D; color: #E2D9FF; }
  .vk-dark .vk-stats { background: rgba(30,16,64,.7); border-color: #3D2870; }
  .vk-dark .vk-icon-btn { background: rgba(124,58,237,.15); }
  .vk-dark .vk-icon-btn:hover { background: rgba(124,58,237,.28); }
  .vk-dark body { background: #0C0818; }

  /* ── stats bar ── */
  .vk-stats {
    display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px;
    padding: 10px 14px;
    background: rgba(255,255,255,.7);
    backdrop-filter: blur(8px);
    border-radius: 16px;
    border: 1px solid #EDE9FE;
    box-shadow: var(--shadow-sm);
  }
  .vk-stat-pill {
    border-radius: 50px; padding: 4px 13px; font-size: 12px; font-weight: 700;
    display: flex; align-items: center; gap: 5px;
    transition: transform .15s;
  }
  .vk-stat-pill:hover { transform: scale(1.05); }

  /* ── speed control ── */
  .vk-speed-wrap {
    display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700;
  }
  .vk-speed-wrap input[type=range] { accent-color: var(--c1); cursor: pointer; }

  /* ── flashcard flip ── */
  .vk-flashcard-scene {
    perspective: 900px;
    width: 100%; height: 100%;
  }
  .vk-flashcard-inner {
    position: relative;
    width: 100%; height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.55s cubic-bezier(.4,2,.6,1);
    cursor: pointer;
  }
  .vk-flashcard-inner.flipped { transform: rotateY(180deg); }
  .vk-flashcard-face {
    position: absolute; inset: 0;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    border-radius: 20px;
    padding: 18px;
    box-shadow: var(--shadow-md);
    display: flex; flex-direction: column; justify-content: space-between;
  }
  .vk-flashcard-back { transform: rotateY(180deg); }
  .vk-flip-hint {
    font-size: 10px; font-weight: 700; opacity: 0.45;
    text-align: right; margin-top: 6px; letter-spacing: .5px;
  }

  /* ── word highlight ── */
  .vk-word {
    display: inline;
    border-radius: 4px;
    padding: 0 1px;
    transition: background 0.15s, color 0.15s;
  }
  .vk-word.active {
    background: linear-gradient(90deg,#7C3AED,#EC4899);
    color: #fff !important;
    border-radius: 4px;
    padding: 1px 3px;
  }

  /* ── flashcard mode toggle ── */
  .vk-mode-toggle {
    display: flex; gap: 6px; margin-bottom: 14px;
  }
  .vk-mode-btn {
    border-radius: 50px; font-size: 12px; font-weight: 700;
    padding: 6px 18px; border: 2px solid #7C3AED;
    cursor: pointer; transition: var(--transition);
    background: #fff; color: #7C3AED;
    font-family: 'Baloo 2', sans-serif;
  }
  .vk-mode-btn:hover { background: #EDE9FE; }
  .vk-mode-btn.active {
    background: linear-gradient(135deg,#7C3AED,#A78BFA);
    color: #fff; border-color: transparent;
    box-shadow: 0 3px 12px #7C3AED44;
  }
  .vk-speaking-icon {
    display: inline-flex;
    align-items: flex-end;
    gap: 2px;
    height: 18px;
    margin-left: 8px;
    vertical-align: middle;
  }
  .vk-speaking-icon span {
    display: inline-block;
    width: 3px;
    border-radius: 2px;
    background: linear-gradient(180deg, #7C3AED, #EC4899);
    animation: vk-wave 0.9s ease-in-out infinite;
  }
  .vk-speaking-icon span:nth-child(1) { height: 6px;  animation-delay: 0s; }
  .vk-speaking-icon span:nth-child(2) { height: 14px; animation-delay: 0.15s; }
  .vk-speaking-icon span:nth-child(3) { height: 10px; animation-delay: 0.3s; }
  .vk-speaking-icon span:nth-child(4) { height: 16px; animation-delay: 0.45s; }
  .vk-speaking-icon span:nth-child(5) { height: 8px;  animation-delay: 0.6s; }
  @keyframes vk-wave {
    0%, 100% { transform: scaleY(0.4); opacity: 0.5; }
    50%       { transform: scaleY(1);   opacity: 1; }
  }

  /* ── bottom navigation bar ── */
  .vk-bottom-nav {
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 1000;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-top: 1px solid rgba(124,58,237,.12);
    display: flex; justify-content: space-around; align-items: center;
    padding: 6px 0 env(safe-area-inset-bottom, 6px);
    box-shadow: 0 -2px 20px #7C3AED14;
  }
  .vk-dark .vk-bottom-nav {
    background: rgba(15,10,30,0.92);
    border-top-color: rgba(124,58,237,.2);
  }
  .vk-bnav-item {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    font-size: 10px; font-weight: 700; color: #9CA3AF; cursor: pointer;
    padding: 5px 12px; border-radius: 14px; transition: var(--transition);
    border: none; background: none; position: relative;
  }
  .vk-bnav-item.active {
    color: #7C3AED;
    background: linear-gradient(135deg,#EDE9FE,#FAF5FF);
    box-shadow: 0 2px 8px #7C3AED22;
  }
  .vk-bnav-item.active .vk-bnav-icon { transform: translateY(-2px) scale(1.1); }
  .vk-dark .vk-bnav-item.active { background: #2D1B69; color: #C4B5FD; }
  .vk-bnav-icon { font-size: 20px; line-height: 1; transition: transform .2s; }

  /* ── pull to refresh ── */
  .vk-ptr-indicator {
    text-align: center; padding: 10px; font-size: 13px; font-weight: 700;
    color: #7C3AED; overflow: hidden; transition: max-height .3s, opacity .3s;
  }
  .vk-ptr-spinner {
    display: inline-block;
    animation: vk-spin 0.7s linear infinite;
  }
  @keyframes vk-spin { to { transform: rotate(360deg); } }

  /* ── swipe card ── */
  .vk-swipeable {
    transition: transform 0.2s ease, opacity 0.2s ease;
    touch-action: pan-y;
  }
  .vk-swipe-hint {
    position: absolute; top: 50%; transform: translateY(-50%);
    font-size: 22px; opacity: 0; transition: opacity .2s; pointer-events: none;
    font-weight: 900;
  }
  .vk-swipe-left  { right: 12px; color: #EC4899; }
  .vk-swipe-right { left: 12px;  color: #10B981; }

  /* ── chapter color themes ── */
  .vk-themed-en-0::before { background: #7C3AED !important; }
  .vk-themed-en-1::before { background: #EC4899 !important; }
  .vk-themed-en-2::before { background: #F59E0B !important; }
  .vk-themed-en-3::before { background: #10B981 !important; }
  .vk-themed-en-4::before { background: #3B82F6 !important; }
  .vk-themed-en-5::before { background: #EF4444 !important; }
  .vk-themed-en-6::before { background: #8B5CF6 !important; }
  .vk-themed-en-7::before { background: #14B8A6 !important; }

  /* ── toast ── */
  .vk-toast {
    position: fixed; bottom: 84px; left: 50%; transform: translateX(-50%);
    background: linear-gradient(135deg,#1a1a2e,#2d1b69);
    color: #fff; padding: 10px 22px;
    border-radius: 50px; font-size: 13px; font-weight: 700;
    z-index: 9999; animation: vk-toast-in .3s cubic-bezier(.34,1.56,.64,1);
    white-space: nowrap;
    box-shadow: 0 4px 20px #7C3AED44;
    border: 1px solid rgba(255,255,255,.1);
    font-family: 'Baloo 2', sans-serif;
  }
  @keyframes vk-toast-in {
    from { opacity:0; transform: translateX(-50%) translateY(12px) scale(.95); }
    to   { opacity:1; transform: translateX(-50%) translateY(0)    scale(1);   }
  }

  /* ── share modal ── */
  .vk-share-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.75);
    display: flex; align-items: center; justify-content: center;
    z-index: 3000; padding: 12px;
  }
  .vk-share-modal-box {
    background: #fff; border-radius: 22px; padding: 18px;
    width: 100%; max-width: 500px; max-height: 92vh; overflow-y: auto;
    box-shadow: 0 24px 64px #0006;
    animation: popIn .25s cubic-bezier(.34,1.56,.64,1);
  }
  /* ── generated image card (rendered off-screen for html2canvas) ── */
  .vk-gen-card {
    position: absolute; left: -9999px; top: 0;
    width: 640px; background: #fff;
    font-family: 'Baloo 2', sans-serif;
    border-radius: 0; overflow: hidden;
  }
  /* gradient header bar */
  .vk-gen-header {
    background: linear-gradient(135deg,#7C3AED,#EC4899);
    padding: 14px 24px 12px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .vk-gen-header-brand { color:#fff; font-size:16px; font-weight:800; letter-spacing:.5px; }
  .vk-gen-header-chapter {
    background: rgba(255,255,255,.25); color:#fff;
    border-radius:50px; font-size:11px; font-weight:800;
    padding:3px 14px;
  }
  /* slide image */
  .vk-gen-card-img { width: 100%; max-height:280px; object-fit:cover; display: block; }
  /* body */
  .vk-gen-card-body { padding: 18px 24px 20px; }
  .vk-gen-section {
    margin-bottom: 14px; padding: 12px 16px;
    border-radius: 12px;
  }
  .vk-gen-section-en { background: #F5F3FF; border-left: 4px solid #7C3AED; }
  .vk-gen-section-ta { background: #FFFBEB; border-left: 4px solid #F59E0B; font-family:"Noto Sans Tamil",sans-serif; }
  .vk-gen-lang-badge { font-size:10px; font-weight:800; letter-spacing:1px; margin-bottom:6px; }
  .vk-gen-q { font-weight:800; font-size:14px; color:#1e1b4b; margin-bottom:5px; line-height:1.4; }
  .vk-gen-a { font-size:12px; color:#444; line-height:1.7; }
  /* footer */
  .vk-gen-footer {
    background: linear-gradient(135deg,#7C3AED,#EC4899);
    padding: 10px 24px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .vk-gen-footer span { color:#fff; font-size:11px; font-weight:700; opacity:.9; }
  /* ── canvas preview inside modal ── */
  .vk-canvas-preview {
    width: 100%; border-radius: 14px; margin-bottom: 14px;
    box-shadow: 0 4px 20px #0002; display: block;
  }
  /* ── platform share buttons ── */
  .vk-share-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .vk-share-action-btn {
    flex: 1; min-width: 90px;
    border: none; border-radius: 50px; padding: 10px 12px;
    font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: opacity .2s;
  }
  .vk-share-action-btn:hover { opacity: .85; }
  .vk-share-action-btn:disabled { opacity: .5; cursor: wait; }

  /* ── root bottom padding for nav ── */
  .vk-root { padding-bottom: 80px; }
  .vk-bookmarked::after {
    content: '🔖';
    position: absolute; top: 8px; right: 12px; font-size: 16px;
  }

  /* ── Facebook Login button ── */
  .vk-fb-login-btn {
    display: inline-flex; align-items: center; gap: 8px;
    background: #1877F2; color: #fff;
    border: none; border-radius: 50px;
    padding: 7px 18px; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: background .2s, transform .15s;
    box-shadow: 0 2px 8px #1877F244;
  }
  .vk-fb-login-btn:hover { background: #145DBF; transform: scale(1.04); }


  /* ── Single share button on card ── */
  .vk-share-btn {
    background: linear-gradient(135deg,#7C3AED,#EC4899);
    color: #fff; border: none; border-radius: 50px;
    padding: 5px 14px; font-size: 11px; font-weight: 800;
    cursor: pointer; transition: var(--transition);
    display: inline-flex; align-items: center; gap: 4px;
    box-shadow: 0 2px 8px #7C3AED33;
    letter-spacing: .2px;
    font-family: 'Baloo 2', sans-serif;
  }
  .vk-share-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px #7C3AED55;
  }

  /* ── Messenger chat bubble ── */
  .vk-messenger-bubble {
    position: fixed; bottom: 88px; right: 16px; z-index: 1500;
    width: 54px; height: 54px; border-radius: 50%;
    background: linear-gradient(135deg,#0084FF,#00C6FF);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 4px 20px #0084FF55;
    transition: transform .2s cubic-bezier(.34,1.56,.64,1), box-shadow .2s;
    border: 3px solid rgba(255,255,255,.3);
  }
  .vk-messenger-bubble:hover {
    transform: scale(1.12);
    box-shadow: 0 8px 28px #0084FF77;
  }
  .vk-messenger-bubble svg { width: 28px; height: 28px; }

  .vk-messenger-panel {
    position: fixed; bottom: 152px; right: 16px; z-index: 1500;
    width: 300px; background: #fff; border-radius: 16px;
    box-shadow: 0 8px 32px #0003; overflow: hidden;
    animation: popIn .25s cubic-bezier(.34,1.56,.64,1);
  }
  .vk-messenger-header {
    background: linear-gradient(90deg,#0084FF,#00C6FF);
    padding: 12px 16px; display: flex; align-items: center; gap: 10px;
    color: #fff; font-weight: 700; font-size: 14px;
  }
  .vk-messenger-header img { width: 32px; height: 32px; border-radius: 50%; border: 2px solid #fff; }
  .vk-messenger-header-close {
    margin-left: auto; background: none; border: none; color: #fff;
    font-size: 18px; cursor: pointer; line-height:1;
  }
  .vk-messenger-body {
    padding: 14px 16px; font-size: 13px; color: #374151; line-height: 1.6;
  }
  .vk-messenger-body .vk-msg-bubble {
    background: #F0F4FF; border-radius: 12px 12px 12px 4px;
    padding: 10px 14px; margin-bottom: 10px; font-size: 13px;
    color: #1e1b4b; display: inline-block; max-width: 90%;
  }
  .vk-messenger-open-btn {
    display: block; width: calc(100% - 32px); margin: 0 16px 14px;
    background: linear-gradient(90deg,#0084FF,#00C6FF); color: #fff;
    border: none; border-radius: 50px; padding: 10px;
    font-weight: 700; font-size: 13px; cursor: pointer;
    transition: opacity .2s;
  }
  .vk-messenger-open-btn:hover { opacity: .88; }
`;

export default function VKTemplate1({ rows = [] }) {
  // ── STATE ──────────────────────────────────────────
  const [slideImage, setSlideImage] = useState(null);
  const [search, setSearch] = useState("");
  const [readModal, setReadModal] = useState(false);
  const [readContent, setReadContent] = useState({});
  const [tamilModal, setTamilModal] = useState(false);
  const [tamilContent, setTamilContent] = useState({});
  const [showContact, setShowContact] = useState(false);
  const [subject, setSubject] = useState("");
  const [comments, setComments] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [filterBookmarked, setFilterBookmarked] = useState(false);
  const [cardMode, setCardMode] = useState("normal");
  const [flippedCards, setFlippedCards] = useState({});
  const toggleFlip = (id) => setFlippedCards(prev => ({ ...prev, [id]: !prev[id] }));

  // ── WORD HIGHLIGHT ─────────────────────────────────
  const [highlightWord, setHighlightWord] = useState(-1);
  const [highlightPhase, setHighlightPhase] = useState("q");

  // ── BOTTOM NAV ─────────────────────────────────────
  const [activeNav, setActiveNav] = useState("home"); // home | bookmarks | search | play | settings
  const [showMessenger, setShowMessenger] = useState(false);
  const [shareModalRow, setShareModalRow] = useState(null);
  const [shareModalLang, setShareModalLang] = useState("en"); // "en" | "ta" | "both"
  const [generatedImg, setGeneratedImg] = useState(null);    // base64 data URL of generated card
  const [imgGenerating, setImgGenerating] = useState(false);
  const shareCardRef = useRef(null); // ref to the off-screen card div

  // ── PULL TO REFRESH ────────────────────────────────
  const [isPulling, setIsPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const pullDelta = useRef(0);

  // ── SWIPE GESTURES ─────────────────────────────────
  const swipeStartX = useRef({});
  const swipeDeltaX = useRef({});
  const [swipeStates, setSwipeStates] = useState({}); // { rowId: deltaX }

  // ── TOAST ──────────────────────────────────────────
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const chapters = [...new Set(rows.map((r) => r.chapter))];
  const [chapterFilter, setChapterFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // ── PICK & SHARE ───────────────────────────────────
  const [pickMode, setPickMode] = useState(false);
  const [pickedIds, setPickedIds] = useState(new Set());

  const encodePickIds = (ids) => {
    const raw = ids.join(",");
    return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  };

  const togglePick = (id) => {
    setPickedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 10) { alert("Maximum 10 questions can be selected at once"); return prev; }
        next.add(id);
      }
      return next;
    });
  };

  const exitPickMode = () => { setPickMode(false); setPickedIds(new Set()); };

  const handleSharePickWhatsApp = () => {
    if (pickedIds.size === 0) return;
    const encoded = encodePickIds([...pickedIds]);
    const url = window.location.origin + "/shared-twomark?q=" + encoded;
    const text = "📚 " + pickedIds.size + " important two-mark questions shared via VirtualKalvi!\n\nPractice here 👇\n" + url;
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  };

  // ── PLAY MODAL ─────────────────────────────────────
  const [playModal, setPlayModal] = useState(false);
  const [playIndex, setPlayIndex] = useState(0);
  const [playItems, setPlayItems] = useState([]);
  const [progress, setProgress] = useState(0);
  const [playLang, setPlayLang] = useState("en");
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const pauseReading = () => { speechSynthesis.pause(); setIsPaused(true); setIsSpeaking(false); };
  const resumeReading = () => { speechSynthesis.resume(); setIsPaused(false); setIsSpeaking(true); };

  const modalContentRef = useRef(null);
  const cancelledRef = useRef(false);

  // ── VOICES ─────────────────────────────────────────
  // Keep voice list in a ref so speakUtter always reads the latest value
  const voicesRef = useRef([]);
  const [tamilVoice,     setTamilVoice]     = useState(null);
  const [tamilVoiceLabel, setTamilVoiceLabel] = useState("detecting...");
  const [showVoiceDebug, setShowVoiceDebug]   = useState(false);

  useEffect(() => {
    const loadVoices = () => {
      const all = window.speechSynthesis.getVoices();
      voicesRef.current = all;

      const ta = all.find((v) => v.lang === "ta-IN")
               || all.find((v) => v.lang.startsWith("ta"));

      // keep in state only for display / debug
      setTamilVoice(ta || null);
      setTamilVoiceLabel(ta ? `✅ ${ta.name} (${ta.lang})` : "❌ No Tamil voice found");
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  // ── SEARCH / FILTER / PAGINATION ──────────────────
  const pageSize = 5;
  const [page, setPage] = useState(1);

  // ── DRAG STATE for floating bar ───────────────────
  const [barPos, setBarPos] = useState({ x: window.innerWidth - 38, y: 120 });

  if (!rows || rows.length === 0) return <div style={{padding:32,textAlign:"center",color:"#7C3AED",fontFamily:"Baloo 2,sans-serif"}}>No data available</div>;

  const openSlide = (img) => {
    if (!img) return alert("No slide available");
    setSlideImage(`/resources/${img}`);
  };

  let filtered = rows.filter((r) => {
    const s = search.toLowerCase();
    const rowId = r.id || r.question_eng;
    const ch  = (r.chapter        || "").toLowerCase();
    const qen = (r.question_eng   || "").toLowerCase();
    const aen = (r.answer_english || "").toLowerCase();
    const qta = (r.question_tamil || "").toLowerCase();
    const ata = (r.answer_tamil   || "").toLowerCase();
    const matchesSearch =
      ch.includes(s) || qen.includes(s) || aen.includes(s) ||
      qta.includes(s) || ata.includes(s);
    const matchesChapter = !chapterFilter || ch === chapterFilter.toLowerCase();
    const matchesBookmark = !filterBookmarked || bookmarks.includes(rowId);
    return matchesSearch && matchesChapter && matchesBookmark;
  });

  const selectAll = () => setSelected(filtered.map((_, i) => i));
  const removeAllSelection = () => setSelected([]);
  const totalPages = Math.ceil(filtered.length / pageSize);
  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  // ── BOOKMARK ───────────────────────────────────────
  const toggleBookmark = (rowId) =>
    setBookmarks((prev) => prev.includes(rowId) ? prev.filter((x) => x !== rowId) : [...prev, rowId]);
  const bookmarkedRows = rows.filter((r) => bookmarks.includes(r.id || r.question_eng));

  // ── helper: speak one utterance ───────────────────
  const speakUtter = (text, lang) => {
    if (!text || !text.trim()) return null;

    // Always read voices fresh from ref — avoids stale closure bug
    const all = voicesRef.current.length
      ? voicesRef.current
      : window.speechSynthesis.getVoices();

    const ta = all.find((v) => v.lang === "ta-IN")
             || all.find((v) => v.lang.startsWith("ta"));
    const en = all.find((v) => v.lang === "en-US")
             || all.find((v) => v.lang.startsWith("en"));

    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = speechRate;

    if (lang === "ta") {
      utter.lang = "ta-IN";
      if (ta) utter.voice = ta;
    } else {
      utter.lang = "en-US";
      if (en) utter.voice = en;
    }
    return utter;
  };

  // ── PLAYBACK ENGINE ────────────────────────────────
  const autoScrollTo = (selector) => {
    if (!modalContentRef.current) return;
    const el = modalContentRef.current.querySelector(selector);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const playSpeech = (items, index, lang, repeatPhase = 0) => {
    if (cancelledRef.current) return;
    const row = items[index].row;

    const question = lang === "ta" ? row.question_tamil_read : row.question_eng_read;
    const answer   = lang === "ta" ? row.answer_tamil_read   : row.answer_english_read;

    const utterQ = speakUtter(question, lang);
    if (!utterQ) return;

    utterQ.onstart = () => { setIsSpeaking(true); setHighlightPhase("q"); setHighlightWord(0); autoScrollTo(".vk-q"); };
    utterQ.onboundary = (e) => {
      if (e.name === "word") {
        const wordIndex = (question.slice(0, e.charIndex).match(/\S+/g) || []).length;
        setHighlightWord(wordIndex);
      }
    };

    utterQ.onend = () => {
      setHighlightWord(-1);
      if (cancelledRef.current) return;
      setTimeout(() => {
        if (cancelledRef.current) return;
        autoScrollTo(".vk-a");
        const utterA = speakUtter(answer, lang);
        if (!utterA) return;

        utterA.onstart = () => { setIsSpeaking(true); setHighlightPhase("a"); setHighlightWord(0); };
        utterA.onboundary = (e) => {
          if (e.name === "word") {
            const wordIndex = (answer.slice(0, e.charIndex).match(/\S+/g) || []).length;
            setHighlightWord(wordIndex);
          }
        };
        utterA.onend = () => {
          setHighlightWord(-1);
          if (cancelledRef.current) { setIsSpeaking(false); return; }
          if (repeatEnabled && repeatPhase < 1) {
            playSpeech(items, index, lang, repeatPhase + 1);
            return;
          }
          const next = index + 1;
          setPlayIndex(next);
          setProgress((next / items.length) * 100);
          if (next < items.length) {
            setTimeout(() => { if (!cancelledRef.current) playSpeech(items, next, lang, 0); }, 600);
          } else {
            setIsSpeaking(false);
          }
        };
        speechSynthesis.speak(utterA);
      }, 800);
    };

    utterQ.onerror = (e) => {
      if (cancelledRef.current) return;
      console.warn("TTS error on question:", e.error, "lang:", lang);
      setTimeout(() => {
        if (cancelledRef.current) return;
        autoScrollTo(".vk-a");
        const utterA = speakUtter(answer, lang);
        if (utterA) speechSynthesis.speak(utterA);
      }, 1000);
    };

    speechSynthesis.speak(utterQ);
  };

  const playSelected = (lang) => {
    cancelledRef.current = false;
    speechSynthesis.cancel();
    const items = filtered.map((row, index) => ({ row, index })).filter((i) => selected.includes(i.index));
    if (items.length === 0) return alert("Please select at least one question");
    setPlayLang(lang);
    setPlayItems(items);
    setPlayIndex(0);
    setProgress(0);
    setIsSpeaking(false);
    setPlayModal(true);
    setTimeout(() => playSpeech(items, 0, lang, 0), 50);
  };

  const closePlayModal = () => { cancelledRef.current = true; speechSynthesis.cancel(); setIsSpeaking(false); setHighlightWord(-1); setPlayModal(false); };

  // ── READ MODALS ────────────────────────────────────
  const openReadSlide = (row) => {
    setReadContent({ question: row.question_eng, answer: row.answer_english });
    setReadModal(true);
    speechSynthesis.cancel();
    cancelledRef.current = false;
    setIsSpeaking(false);
    const utterQ = speakUtter(row.question_eng_read, "en");
    utterQ.onstart = () => setIsSpeaking(true);
    utterQ.onend = () => {
      if (cancelledRef.current) { setIsSpeaking(false); return; }
      setTimeout(() => {
        if (cancelledRef.current) { setIsSpeaking(false); return; }
        const utterA = speakUtter(row.answer_english_read, "en");
        utterA.onstart = () => setIsSpeaking(true);
        utterA.onend = () => setIsSpeaking(false);
        speechSynthesis.speak(utterA);
      }, 800);
    };
    speechSynthesis.speak(utterQ);
  };
  const closeReadModal = () => { cancelledRef.current = true; speechSynthesis.cancel(); setIsSpeaking(false); setReadModal(false); };

  const openTamilReadSlide = (row) => {
    setTamilContent({ question: row.question_tamil, answer: row.answer_tamil});
    setTamilModal(true);
    speechSynthesis.cancel();
    cancelledRef.current = false;
    setIsSpeaking(false);
    const utterQ = speakUtter(row.question_tamil_read, "ta");
    utterQ.onstart = () => setIsSpeaking(true);
    utterQ.onend = () => {
      if (cancelledRef.current) { setIsSpeaking(false); return; }
      setTimeout(() => {
        if (cancelledRef.current) { setIsSpeaking(false); return; }
        const utterA = speakUtter(row.answer_tamil_read, "ta");
        utterA.onstart = () => setIsSpeaking(true);
        utterA.onend = () => setIsSpeaking(false);
        speechSynthesis.speak(utterA);
      }, 800);
    };
    speechSynthesis.speak(utterQ);
  };
  const closeTamilModal = () => { cancelledRef.current = true; speechSynthesis.cancel(); setIsSpeaking(false); setTamilModal(false); };

  // ── CONTACT ────────────────────────────────────────
  const handleSendMail = async () => {
    if (!subject.trim() || !comments.trim()) return alert("Please fill all fields");
    const res = await sendContactMail(subject, comments);
    alert(res.message);
    if (res.success) { setShowContact(false); setSubject(""); setComments(""); }
  };

  const onBarMouseDown = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const onMove = (mv) => setBarPos({ x: mv.clientX - offX, y: mv.clientY - offY });
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onBarTouchStart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const t = e.touches[0];
    const offX = t.clientX - rect.left;
    const offY = t.clientY - rect.top;
    const onMove = (mv) => { const t2 = mv.touches[0]; setBarPos({ x: t2.clientX - offX, y: t2.clientY - offY }); };
    const onEnd = () => { window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onEnd); };
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
  };


  const pillColors = ["#7C3AED","#EC4899","#F59E0B","#10B981","#3B82F6","#EF4444","#8B5CF6","#14B8A6"];
  const getPillColor = (i) => pillColors[i % pillColors.length];
  const getChapterIndex = (ch) => chapters.indexOf(ch);

  // ── PULL TO REFRESH ────────────────────────────────
  const onPtrTouchStart = (e) => { pullStartY.current = e.touches[0].clientY; };
  const onPtrTouchMove = (e) => {
    if (window.scrollY > 0) return;
    const delta = e.touches[0].clientY - pullStartY.current;
    if (delta > 0) { pullDelta.current = delta; setIsPulling(delta > 50); }
  };
  const onPtrTouchEnd = () => {
    if (pullDelta.current > 80) {
      setRefreshing(true); setIsPulling(false);
      setTimeout(() => { setRefreshing(false); setPage(1); setSearch(""); setChapterFilter(""); showToast("✅ Refreshed!"); }, 1200);
    } else { setIsPulling(false); }
    pullDelta.current = 0;
  };

  // ── SWIPE GESTURES ─────────────────────────────────
  const onCardTouchStart = (e, rowId) => { swipeStartX.current[rowId] = e.touches[0].clientX; };
  const onCardTouchMove = (e, rowId) => {
    const dx = e.touches[0].clientX - (swipeStartX.current[rowId] || 0);
    swipeDeltaX.current[rowId] = dx;
    setSwipeStates(prev => ({ ...prev, [rowId]: dx }));
  };
  const onCardTouchEnd = (e, rowId, row) => {
    const dx = swipeDeltaX.current[rowId] || 0;
    if (dx < -60) { toggleBookmark(rowId); showToast("🔖 Bookmarked!"); }
    else if (dx > 60) { openReadSlide(row); showToast("📢 Reading..."); }
    swipeDeltaX.current[rowId] = 0;
    setSwipeStates(prev => ({ ...prev, [rowId]: 0 }));
  };

  // ── WHATSAPP BRANDING ──────────────────────────────
  const WA_SITE_NAME = "Virtulkalvi";
  const WA_URL       = "https://virtualkalvi.com";
  const WA_FOOTER    = `\n\n--------------\n*${WA_SITE_NAME}*\n${WA_URL}\n--------------`;

  // ── SOCIAL — no App ID needed ──────────────────────
  const SITE_URL   = WA_URL;
  const FB_PAGE_ID = "61554849740185"; // your real Facebook Page ID

  // Open Messenger chat directly using Page ID — no App ID needed
  const openMessenger = () => {
    window.open(`https://m.me/${FB_PAGE_ID}`, "_blank");
  };

  // Use this instead of encodeURIComponent — fixes emoji display on all devices
  const waShare = (text) => {
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  };

  // ── Load html2canvas once ──────────────────────────
  const loadHtml2Canvas = () => new Promise((resolve) => {
    if (window.html2canvas) return resolve(window.html2canvas);
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    s.onload = () => resolve(window.html2canvas);
    document.head.appendChild(s);
  });

  // ── Generate card image from the hidden div ───────
  const generateCardImage = async () => {
    if (!shareCardRef.current) return null;
    setImgGenerating(true);
    setGeneratedImg(null);
    try {
      const h2c = await loadHtml2Canvas();
      const canvas = await h2c(shareCardRef.current, {
        scale: 2, useCORS: true, allowTaint: true,
        backgroundColor: "#ffffff", logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      setGeneratedImg(dataUrl);
      return dataUrl;
    } catch (e) {
      console.error("html2canvas error:", e);
      showToast("⚠️ Could not generate image");
      return null;
    } finally {
      setImgGenerating(false);
    }
  };

  // ── Build share text per language ────────────────
  const buildShareText = (row, lang = "both") => {
    const header = `📚 ${WA_SITE_NAME} | ${row.chapter}\n`;
    const en = `\n🇬🇧 Q: ${row.question_eng}\n✅ A: ${row.answer_english}`;
    const ta = `\n🇮🇳 Q: ${row.question_tamil}\n✅ A: ${row.answer_tamil}`;
    const footer = `\n\n🌐 ${WA_URL}`;
    if (lang === "en") return header + en + footer;
    if (lang === "ta") return header + ta + footer;
    return header + en + "\n" + ta + footer;
  };

  // ── WhatsApp: send text ───────────────────────────
  const shareWA = (row, lang = "both") => {
    waShare(buildShareText(row, lang));
    setShareModalRow(null);
  };

  // ── Convert base64 dataURL → File object ─────────
  const dataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  // ── Native share sheet (mobile) → fallback download (desktop) ─────
  const shareImageNative = async (dataUrl, row, platform) => {
    const filename = `VKStudy_${row.chapter.replace(/\s+/g,"_")}.png`;
    const text     = buildShareText(row, shareModalLang);

    // ── Mobile: use Web Share API with image file ──
    if (navigator.canShare) {
      const file = dataUrlToFile(dataUrl, filename);
      const shareData = { files: [file], text };
      if (navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          setShareModalRow(null);
          return;
        } catch (e) {
          if (e.name !== "AbortError") console.warn("share error:", e);
          return; // user cancelled
        }
      }
    }

    // ── Fallback: download image + open platform ───
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
    showToast("✅ Image saved!");
    const urls = {
      facebook:  "https://www.facebook.com/",
      instagram: "https://www.instagram.com/",
    };
    if (urls[platform]) {
      setTimeout(() => window.open(urls[platform], "_blank"), 800);
    }
  };

  // ── Plain download (for the Save button) ──────────
  const downloadImage = (dataUrl, row) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `VKStudy_${row.chapter.replace(/\s+/g,"_")}.png`;
    a.click();
    showToast("✅ Image saved to device!");
  };

  // ── Open share modal ──────────────────────────────
  const openShareModal = (row, lang = "both") => {
    setShareModalRow(row);
    setShareModalLang(lang);
    setGeneratedImg(null);
    // auto-generate after DOM renders the off-screen card
    setTimeout(() => generateCardImage(), 120);
  };



  // ── EXPORT BOOKMARKS TO PDF ────────────────────────
  const exportBookmarksPDF = () => {
    if (bookmarkedRows.length === 0) return showToast("⚠️ No bookmarks to export!");
    const win = window.open("", "_blank");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;max-width:800px;margin:0 auto}
      h1{color:#7C3AED;border-bottom:3px solid #EC4899;padding-bottom:8px;margin-bottom:4px}
      .meta{color:#888;font-size:12px;margin-bottom:24px}
      .card{border:1px solid #EDE9FE;border-radius:12px;padding:16px;margin-bottom:16px;page-break-inside:avoid}
      .ch{background:#7C3AED;color:#fff;border-radius:50px;font-size:11px;padding:2px 10px;display:inline-block;margin-bottom:8px}
      .q{font-weight:700;color:#1e1b4b;margin-bottom:6px;font-size:14px}
      .a{color:#555;font-size:13px;line-height:1.6}
      .ta{border-top:1px dashed #FDE68A;margin-top:10px;padding-top:10px;font-size:13px}
      .footer{margin-top:32px;border-top:2px solid #EDE9FE;padding-top:14px;text-align:center;color:#7C3AED;font-size:12px}
    </style></head><body>
    <h1>📚 ${WA_SITE_NAME} — Bookmarked Questions</h1>
    <div class="meta">
      📖 Subject: Subject &nbsp;|&nbsp; 📅 Exported: ${new Date().toLocaleDateString()} &nbsp;|&nbsp; 🌐 ${WA_URL}
    </div>
    ${bookmarkedRows.map(r => `
      <div class="card">
        <span class="ch">${r.chapter}</span>
        <div class="q">🔵 ${r.question_eng}</div>
        <div class="a">${r.answer_english}</div>
        <div class="ta">🟡 ${r.question_tamil}<br/><span style="color:#555">${r.answer_tamil}</span></div>
      </div>`).join("")}
    <div class="footer">
      📚 <strong>${WA_SITE_NAME}</strong> &nbsp;|&nbsp; 🌐 ${WA_URL}
    </div>
    </body></html>`;
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
    showToast("📄 PDF ready to save!");
  };

  // ── STATS ──────────────────────────────────────────
  const totalQ = rows.length;
  const filteredQ = filtered.length;
  const selectedQ = selected.length;

  // ─────────────────────────────────────────────────────────────
  // JSX
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div
        className={`vk-root${darkMode ? " vk-dark" : ""}`}
        style={{ fontSize }}
        onTouchStart={onPtrTouchStart}
        onTouchMove={onPtrTouchMove}
        onTouchEnd={onPtrTouchEnd}
      >
        {/* ── PULL TO REFRESH INDICATOR ── */}
        {(isPulling || refreshing) && (
          <div className="vk-ptr-indicator">
            {refreshing
              ? <span className="vk-ptr-spinner">🔄</span>
              : "↓ Release to refresh"}
          </div>
        )}

        {/* ── TOP BAR ── */}
        <div style={{
          background:"linear-gradient(135deg,#7C3AED 0%,#EC4899 60%,#F59E0B 100%)",
          borderRadius:20, padding:"14px 18px 16px", marginBottom:16,
          position:"relative", overflow:"hidden",
        }}>
          {/* decorative dots */}
          <div style={{ position:"absolute", inset:0, opacity:.08,
            backgroundImage:"radial-gradient(#fff 1px,transparent 1px)",
            backgroundSize:"20px 20px", pointerEvents:"none" }}/>
          {/* Row 1: Logo + Bookmark */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, position:"relative" }}>
            <div style={{ fontFamily:"Baloo 2,sans-serif", fontWeight:800, fontSize:22, color:"#fff",
              textShadow:"0 2px 10px rgba(0,0,0,.2)", display:"flex", alignItems:"center", gap:8 }}>
              📚 VK Study
            </div>
            <button
              style={{ background:"rgba(255,255,255,.25)", backdropFilter:"blur(8px)",
                color:"#fff", border:"1px solid rgba(255,255,255,.3)", borderRadius:50,
                padding:"6px 16px", cursor:"pointer", fontWeight:700, fontSize:13,
                display:"flex", alignItems:"center", gap:5 }}
              onClick={() => setShowBookmarks(!showBookmarks)}
            >
              🔖 {bookmarks.length}
            </button>
          </div>
          {/* Row 2: Controls */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", position:"relative" }}>
            <button
              style={{ background:"rgba(255,255,255,.2)", backdropFilter:"blur(6px)",
                color:"#fff", border:"1px solid rgba(255,255,255,.25)",
                borderRadius:50, padding:"5px 14px", cursor:"pointer", fontWeight:700, fontSize:12,
                fontFamily:"Baloo 2,sans-serif" }}
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? "☀️ Light" : "🌙 Dark"}
            </button>
            <button style={{ background:"rgba(255,255,255,.2)", backdropFilter:"blur(6px)",
              color:"#fff", border:"1px solid rgba(255,255,255,.25)",
              borderRadius:50, padding:"5px 14px", cursor:"pointer", fontWeight:700, fontSize:12,
              fontFamily:"Baloo 2,sans-serif" }}
              onClick={() => setFontSize(f => Math.min(f + 1, 20))}>A+</button>
            <button style={{ background:"rgba(255,255,255,.2)", backdropFilter:"blur(6px)",
              color:"#fff", border:"1px solid rgba(255,255,255,.25)",
              borderRadius:50, padding:"5px 14px", cursor:"pointer", fontWeight:700, fontSize:12,
              fontFamily:"Baloo 2,sans-serif" }}
              onClick={() => setFontSize(f => Math.max(f - 1, 11))}>A-</button>

            {/* ── Follow on Facebook ── */}
            <button className="vk-fb-login-btn"
              style={{ background:"rgba(255,255,255,.22)", backdropFilter:"blur(6px)",
                border:"1px solid rgba(255,255,255,.35)", boxShadow:"none" }}
              onClick={() => window.open(`https://www.facebook.com/profile.php?id=${FB_PAGE_ID}`, "_blank")}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
              FB Follow
            </button>
            {/* ── Message Us on Messenger ── */}
            <button style={{ background:"rgba(255,255,255,.22)", backdropFilter:"blur(6px)",
              color:"#fff", border:"1px solid rgba(255,255,255,.35)",
              borderRadius:50, padding:"5px 14px", cursor:"pointer",
              fontWeight:700, fontSize:12, display:"inline-flex", alignItems:"center", gap:5,
              fontFamily:"Baloo 2,sans-serif" }}
              onClick={openMessenger}>
              💬 Message
            </button>
          </div>
        </div>

        {/* ── STATS ── */}
        <div className="vk-stats">
          <span className="vk-stat-pill" style={{ background:"#EDE9FE", color:"#7C3AED" }}>📊 Total: {totalQ}</span>
          <span className="vk-stat-pill" style={{ background:"#D1FAE5", color:"#10B981" }}>🔍 Filtered: {filteredQ}</span>
          <span className="vk-stat-pill" style={{ background:"#FEF3C7", color:"#D97706" }}>✅ Selected: {selectedQ}</span>
          <span className="vk-stat-pill" style={{ background:"#FCE7F3", color:"#EC4899" }}>📖 Page: {page}/{totalPages}</span>
          {/* Tamil voice status — tap to see all voices */}
          <span className="vk-stat-pill" style={{ background: tamilVoice ? "#D1FAE5":"#FEE2E2",
            color: tamilVoice ? "#10B981":"#EF4444", cursor:"pointer" }}
            onClick={() => setShowVoiceDebug(true)}>
            🗣 Tamil: {tamilVoiceLabel}
          </span>
        </div>

        {/* ── NAV TOOLBAR (draggable floating) ── */}
        <div
          className="vk-floating-grid"
          style={{ left: barPos.x, top: barPos.y }}
          onMouseDown={onBarMouseDown}
          onTouchStart={onBarTouchStart}
        >
          {[
            { icon:"🏠", color:"#7C3AED", title:"Home", action:() => window.location.href="/" },
            { icon:"✉️", color:"#EC4899", title:"Contact", action:() => setShowContact(true) },
            { icon:"⬆️", color:"#10B981", title:"Top", action:() => window.scrollTo({top:0,behavior:"smooth"}) },
            { icon:"⬇️", color:"#3B82F6", title:"Bottom", action:() => window.scrollTo({top:document.body.scrollHeight,behavior:"smooth"}) },
          ].map((item, i) => (
            <div key={i} className="vk-float-item" title={item.title}
              style={{ background: item.color }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={item.action}>
              {item.icon}
            </div>
          ))}
        </div>

        {/* ── SEARCH ── */}
        <input
          type="text"
          className="vk-search"
          placeholder="⌨️ Search chapter, question, answer..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ marginBottom: 12 }}
        />

        {/* ── SPEED CONTROL ── */}
        <div className="vk-speed-wrap" style={{ marginBottom: 12 }}>
          <span>🐢</span>
          <input type="range" min="0.5" max="2" step="0.1" value={speechRate}
            onChange={(e) => setSpeechRate(parseFloat(e.target.value))} style={{ width:120 }} />
          <span>🐇</span>
          <span style={{ color:"#7C3AED", fontSize:13 }}>Speed: {speechRate}×</span>
          <label style={{ marginLeft:14, fontSize:13, display:"flex", alignItems:"center", gap:5 }}>
            <input type="checkbox" checked={repeatEnabled} onChange={() => setRepeatEnabled(!repeatEnabled)} />
            🔁 Repeat
          </label>
        </div>

        {/* ── CHAPTER FILTER ── */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:12, alignItems:"center" }}>
          {chapters.map((ch, i) => (
            <button
              key={i}
              className={`vk-chapter-pill${chapterFilter === ch ? " active" : ""}`}
              style={{
                background: chapterFilter === ch ? getPillColor(i) : `${getPillColor(i)}22`,
                color: chapterFilter === ch ? "#fff" : getPillColor(i),
                borderColor: getPillColor(i),
              }}
              onClick={() => { setChapterFilter(chapterFilter === ch ? "" : ch); setPage(1); }}
            >
              {ch}
            </button>
          ))}
          {chapterFilter && (
            <button className="vk-chapter-pill" style={{ background:"#FEE2E2", color:"#EF4444", borderColor:"#EF4444" }}
              onClick={() => { setChapterFilter(""); setPage(1); }}>✕ Clear</button>
          )}
          {/* Bookmark Filter Toggle — next to chapter pills */}
          <button
            className={`vk-chapter-pill${filterBookmarked ? " active" : ""}`}
            style={{
              background: filterBookmarked ? "#EC4899" : "#FCE7F322",
              color: filterBookmarked ? "#fff" : "#EC4899",
              borderColor: "#EC4899",
              marginLeft: "auto",
            }}
            onClick={() => { setFilterBookmarked(!filterBookmarked); setPage(1); }}
          >
            {filterBookmarked ? "🔖 Bookmarked Only" : "🔖 Show Bookmarked"}
          </button>
        </div>
        


        {/* ── SELECTION BUTTONS ── */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:14 }}>
          <button style={{ background:"#10B981", color:"#fff", border:"none", borderRadius:50,
            padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
            onClick={selectAll}>☑️ Select All</button>
          <button style={{ background:"#EF4444", color:"#fff", border:"none", borderRadius:50,
            padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
            onClick={removeAllSelection}>✕ Remove All</button>
          <button style={{ background:"#7C3AED", color:"#fff", border:"none", borderRadius:50,
            padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
            onClick={() => playSelected("en")}>🔊 Play ENG</button>
          <button style={{ background:"#F59E0B", color:"#fff", border:"none", borderRadius:50,
            padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
            onClick={() => playSelected("ta")}>🔊 Play தமிழ்</button>
          {/* Pick & Share */}
          <button style={{
            background: pickMode ? "linear-gradient(90deg,#2563EB,#7C3AED)" : "linear-gradient(90deg,#7C3AED,#EC4899)",
            color:"#fff", border:"none", borderRadius:50,
            padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13,
            boxShadow: pickMode ? "0 0 0 3px rgba(37,99,235,0.35)" : "none",
            transition:"all 0.2s"
          }}
          onClick={() => { if (pickMode) exitPickMode(); else setPickMode(true); }}>
            {pickMode
              ? (pickedIds.size > 0 ? `📌 ${pickedIds.size}/10 Picking…` : "📌 Picking… (tap cards)")
              : "📌 Pick & Share"}
          </button>
        </div>

        {/* ── MODE TOGGLE ── */}
        <div className="vk-mode-toggle">
          <button className={`vk-mode-btn${cardMode==="normal"?" active":""}`} onClick={() => setCardMode("normal")}>📄 Normal</button>
          <button className={`vk-mode-btn${cardMode==="flashcard"?" active":""}`} onClick={() => { setCardMode("flashcard"); setFlippedCards({}); }}>🃏 Flashcard</button>
        </div>

        {/* ── QUESTION CARDS ── */}
        {paginated.map((row, i) => {
          const rowId = row.id || row.question_eng;
          const isBookmarked = bookmarks.includes(rowId);
          if (cardMode === "flashcard") {
            return (
              <div key={i} style={{ display:"flex", gap:10, marginBottom:14 }}>
                <div style={{ paddingTop:18 }}>
                  <input type="checkbox" checked={selected.includes(start + i)}
                    onChange={() => toggleSelect(start + i)}
                    style={{ width:18, height:18, accentColor:"#7C3AED", cursor:"pointer" }} />
                </div>
                <div style={{ flex:1, display:"flex", gap:10, flexWrap:"wrap" }}>

                  {/* English Flashcard */}
                  <div style={{ flex:"1 1 280px", minHeight:160 }} onClick={() => toggleFlip(rowId + "_en")}>
                    <div className="vk-flashcard-scene" style={{ minHeight:160 }}>
                      <div className={`vk-flashcard-inner${flippedCards[rowId+"_en"] ? " flipped" : ""}`}>
                        {/* Front: Question */}
                        <div className="vk-flashcard-face vk-card-en" style={{ background:"linear-gradient(135deg,#EDE9FE,#FAF5FF)" }}>
                          <div>
                            <div style={{ fontSize:10, fontWeight:800, color:"#7C3AED", marginBottom:6, letterSpacing:1 }}>❓ QUESTION</div>
                            <div className="vk-q-text" style={{ fontSize }}>{row.question_eng}</div>
                          </div>
                          <div className="vk-flip-hint" style={{ color:"#7C3AED" }}>Tap to reveal answer ↩</div>
                        </div>
                        {/* Back: Answer */}
                        <div className="vk-flashcard-face vk-flashcard-back vk-card-en" style={{ background:"linear-gradient(135deg,#C4B5FD,#EDE9FE)" }}>
                          <div>
                            <div style={{ fontSize:10, fontWeight:800, color:"#5B21B6", marginBottom:6, letterSpacing:1 }}>✅ ANSWER</div>
                            <div className="vk-a-text" style={{ fontSize: fontSize - 1 }}>{row.answer_english}</div>
                          </div>
                          <div style={{ display:"flex", gap:6, marginTop:8 }}>
                            <button className="vk-icon-btn" title="Read English" onClick={(e) => { e.stopPropagation(); openReadSlide(row); }}>📢</button>
                            <button className="vk-icon-btn" title={isBookmarked?"Remove bookmark":"Bookmark"} onClick={(e) => { e.stopPropagation(); toggleBookmark(rowId); }}>{isBookmarked?"🔖":"📌"}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tamil Flashcard */}
                  <div style={{ flex:"1 1 280px", minHeight:160, fontFamily:"'Noto Sans Tamil',sans-serif" }} onClick={() => toggleFlip(rowId + "_ta")}>
                    <div className="vk-flashcard-scene" style={{ minHeight:160 }}>
                      <div className={`vk-flashcard-inner${flippedCards[rowId+"_ta"] ? " flipped" : ""}`}>
                        <div className="vk-flashcard-face vk-card-ta" style={{ background:"linear-gradient(135deg,#FFF7ED,#FFFBEB)" }}>
                          <div>
                            <div style={{ fontSize:10, fontWeight:800, color:"#D97706", marginBottom:6, letterSpacing:1 }}>❓ கேள்வி</div>
                            <div className="vk-q-text" style={{ fontSize }}>{row.question_tamil}</div>
                          </div>
                          <div className="vk-flip-hint" style={{ color:"#D97706" }}>தட்டி பதிலைக் காண்க ↩</div>
                        </div>
                        <div className="vk-flashcard-face vk-flashcard-back vk-card-ta" style={{ background:"linear-gradient(135deg,#FDE68A,#FFF7ED)" }}>
                          <div>
                            <div style={{ fontSize:10, fontWeight:800, color:"#92400E", marginBottom:6, letterSpacing:1 }}>✅ பதில்</div>
                            <div className="vk-a-text" style={{ fontSize: fontSize - 1 }}>{row.answer_tamil}</div>
                          </div>
                          <div style={{ display:"flex", gap:6, marginTop:8 }}>
                            <button className="vk-icon-btn" title="Read Tamil" onClick={(e) => { e.stopPropagation(); openTamilReadSlide(row); }}>📢</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Meta */}
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", justifyContent:"flex-start", gap:4, paddingTop:4 }}>
                    <span className="vk-badge">{row.chapter}</span>
                    <span className="vk-updated">{row.updated_ago}</span>
                  </div>
                </div>
              </div>
            );
          }

          // ── NORMAL mode ──
          const ci = getChapterIndex(row.chapter);
          const chColor = getPillColor(ci);
          const swipeDx = swipeStates[rowId] || 0;
          const isPicked = pickMode && row.id && pickedIds.has(row.id);
          return (
            <div key={i} style={{
              display:"flex", gap:10, marginBottom:14, position:"relative",
              borderRadius: pickMode ? 14 : 0,
              outline: isPicked ? "2.5px solid #2563EB" : (pickMode ? "2px dashed #93C5FD" : "none"),
              cursor: pickMode ? "pointer" : "default",
              transition: "outline 0.15s",
            }}
            onClick={pickMode ? (e) => { e.stopPropagation(); if (row.id) togglePick(row.id); } : undefined}>

              {/* Pick-mode checkmark badge */}
              {pickMode && (
                <div style={{
                  position:"absolute", top:8, right:8, zIndex:10,
                  width:26, height:26, borderRadius:"50%",
                  background: isPicked ? "#2563EB" : "rgba(255,255,255,0.85)",
                  border: isPicked ? "none" : "2px solid #93C5FD",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, fontWeight:800, color:"#fff",
                  boxShadow:"0 1px 4px rgba(0,0,0,0.15)",
                  transition:"background 0.15s",
                  pointerEvents:"none",
                }}>
                  {isPicked ? "✓" : ""}
                </div>
              )}

              {/* Pick-mode: order badge */}
              {isPicked && (() => {
                const arr = [...pickedIds];
                const idx2 = arr.indexOf(row.id) + 1;
                return (
                  <div style={{
                    position:"absolute", bottom:10, left:44, zIndex:10,
                    background:"#2563EB", color:"#fff", borderRadius:999,
                    fontSize:11, fontWeight:800, padding:"2px 8px",
                    pointerEvents:"none",
                  }}>#{idx2}</div>
                );
              })()}

              {/* checkbox col — hidden in pick mode */}
              {!pickMode && (
              <div style={{ paddingTop:18 }}>
                <input type="checkbox" checked={selected.includes(start + i)}
                  onChange={() => toggleSelect(start + i)}
                  style={{ width:18, height:18, accentColor:"#7C3AED", cursor:"pointer" }} />
              </div>
              )}

              <div style={{ flex:1, display:"flex", gap:10, flexWrap:"wrap" }}>

                {/* English Card — swipeable + chapter themed */}
                <div
                  className={`vk-card vk-card-en vk-themed-en-${ci % 8} vk-swipeable`}
                  style={{ flex:"1 1 280px", transform:`translateX(${swipeDx}px)`, opacity: Math.max(0.6, 1 - Math.abs(swipeDx)/300), position:"relative" }}
                  onTouchStart={(e) => onCardTouchStart(e, rowId)}
                  onTouchMove={(e) => onCardTouchMove(e, rowId)}
                  onTouchEnd={(e) => onCardTouchEnd(e, rowId, row)}
                >
                  <span className="vk-swipe-hint vk-swipe-left" style={{ opacity: swipeDx < -30 ? 1 : 0 }}>🔖</span>
                  <span className="vk-swipe-hint vk-swipe-right" style={{ opacity: swipeDx > 30 ? 1 : 0 }}>📢</span>
                  <div className="vk-q-text" style={{ fontSize }}>{row.question_eng}</div>
                  <div className="vk-a-text" style={{ fontSize: fontSize - 1 }}>{row.answer_english}</div>
                  <div style={{ display:"flex", gap:6, marginTop:8, alignItems:"center", flexWrap:"wrap" }}>
                    <button className="vk-icon-btn" title="Slide" onClick={() => openSlide(row.slide)}>🖼️</button>
                    <button className="vk-icon-btn" title="Read English" onClick={() => openReadSlide(row)}>📢</button>
                    <button className="vk-icon-btn" title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                      onClick={() => toggleBookmark(rowId)}>{isBookmarked ? "🔖" : "📌"}</button>
                    <button className="vk-share-btn" onClick={() => openShareModal(row, "en")}>
                      ↗ Share
                    </button>
                  </div>
                </div>

                {/* Tamil Card */}
                <div className="vk-card vk-card-ta" style={{ flex:"1 1 280px", fontFamily:"'Noto Sans Tamil', sans-serif", borderLeft:`5px solid ${chColor}` }}>
                  <div className="vk-q-text" style={{ fontSize }}>{row.question_tamil}</div>
                  <div className="vk-a-text" style={{ fontSize: fontSize - 1 }}>{row.answer_tamil}</div>
                  <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
                    <button className="vk-icon-btn" title="Slide" onClick={() => openSlide(row.slide_tamil)}>🖼️</button>
                    <button className="vk-icon-btn" title="Read Tamil" onClick={() => openTamilReadSlide(row)}>📢</button>
                    <button className="vk-share-btn" onClick={() => openShareModal(row, "ta")}>
                      ↗ Share
                    </button>
                  </div>
                </div>

                {/* Meta */}
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", justifyContent:"flex-start", gap:4, paddingTop:4 }}>
                  <span className="vk-badge" style={{ background:`linear-gradient(135deg,${chColor},${chColor}99)` }}>{row.chapter}</span>
                  <span className="vk-updated">{row.updated_ago}</span>
                </div>

              </div>
            </div>
          );
        })}

        {/* ── PAGINATION ── */}
        <div style={{ display:"flex", justifyContent:"center", flexWrap:"wrap", gap:6, marginBottom:20 }}>
          <button className="vk-page-btn" disabled={page===1} onClick={() => setPage(1)}>« First</button>
          <button className="vk-page-btn" disabled={page===1} onClick={() => setPage(page-1)}>‹ Prev</button>
          {[page-1,page,page+1].filter(p=>p>=1&&p<=totalPages).map((p,i)=>(
            <button key={i} className={`vk-page-btn${page===p?" active":""}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="vk-page-btn" disabled={page===totalPages} onClick={() => setPage(page+1)}>Next ›</button>
          <button className="vk-page-btn" disabled={page===totalPages} onClick={() => setPage(totalPages)}>Last »</button>
        </div>

        {/* ── SLIDE POPUP ── */}
        {slideImage && (
          <div onClick={() => setSlideImage(null)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.72)", zIndex:9999,
              display:"flex", alignItems:"center", justifyContent:"center", cursor:"zoom-out" }}>
            <img src={slideImage} alt="slide"
              style={{ maxWidth:"92%", maxHeight:"92%", borderRadius:12, boxShadow:"0 4px 40px #000a" }} />
          </div>
        )}

        {/* ── BOOKMARKS PANEL ── */}
        {showBookmarks && (
          <div className="vk-modal-overlay">
            <div className="vk-modal-box" style={{ maxWidth:640 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <h4 style={{ margin:0, color:"#EC4899" }}>🔖 Bookmarked Questions ({bookmarkedRows.length})</h4>
                <button style={{ background:"none", border:"none", fontSize:22, cursor:"pointer" }}
                  onClick={() => setShowBookmarks(false)}>✕</button>
              </div>
              {bookmarkedRows.length === 0
                ? <div style={{ color:"#999", textAlign:"center", padding:20 }}>No bookmarks yet. Click 📌 on any card.</div>
                : bookmarkedRows.map((row, i) => {
                  const ci = getChapterIndex(row.chapter);
                  const cc = getPillColor(ci);
                  return (
                    <div key={i} style={{ background:"#F5F3FF", borderRadius:12, padding:"12px 14px", marginBottom:10, borderLeft:`4px solid ${cc}` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, color:"#4C1D95", fontSize:13 }}>{row.question_eng}</div>
                          <div style={{ color:"#6B7280", fontSize:12, marginTop:4 }}>{row.answer_english}</div>
                          <div style={{ fontSize:11, fontWeight:700, marginTop:6, color:cc }}>{row.chapter}</div>
                        </div>
                        <button title="Share on WhatsApp"
                          style={{ background:"#25D366", color:"#fff", border:"none", borderRadius:50,
                            padding:"5px 10px", fontSize:13, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}
                          onClick={() => shareWA(row, "both")}>📲 WA</button>
                      </div>
                    </div>
                  );
                })
              }
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:14, gap:8, flexWrap:"wrap" }}>
                <button style={{ background:"#25D366", color:"#fff", border:"none", borderRadius:50,
                  padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
                  onClick={() => {
                    const text =
                      `*${WA_SITE_NAME} — Bookmarked Questions*\n\n` +
                      bookmarkedRows.map((r,i) =>
                        `*${i+1}. ${r.chapter}*\n\n` +
                        `--- English ---\n` +
                        `*Q:* ${r.question_eng}\n` +
                        `*A:* ${r.answer_english}\n\n` +
                        `--- Tamil ---\n` +
                        `*Q:* ${r.question_tamil}\n` +
                        `*A:* ${r.answer_tamil}`
                      ).join("\n\n===============\n\n") +
                      WA_FOOTER;
                    waShare(text);
                  }}>
                  📲 Share All on WhatsApp</button>
                <button style={{ background:"#7C3AED", color:"#fff", border:"none", borderRadius:50,
                  padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
                  onClick={exportBookmarksPDF}>📄 Export PDF</button>
                <button style={{ background:"#EF4444", color:"#fff", border:"none", borderRadius:50,
                  padding:"6px 18px", fontWeight:700, cursor:"pointer", fontSize:13 }}
                  onClick={() => setBookmarks([])}>🗑 Clear All</button>
              </div>
            </div>
          </div>
        )}

        {/* ── CONTACT MODAL ── */}
        {showContact && (
          <div className="vk-modal-overlay">
            <div className="vk-modal-box">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <h4 style={{ margin:0, color:"#EC4899" }}>✉️ Contact Us</h4>
                <button style={{ background:"none", border:"none", fontSize:22, cursor:"pointer" }}
                  onClick={() => setShowContact(false)}>✕</button>
              </div>
              <input type="text" style={{ width:"100%", borderRadius:10, border:"2px solid #A78BFA",
                padding:"9px 12px", marginBottom:10, fontSize:14, boxSizing:"border-box" }}
                placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
              <textarea style={{ width:"100%", borderRadius:10, border:"2px solid #A78BFA",
                padding:"9px 12px", marginBottom:10, fontSize:14, resize:"vertical", boxSizing:"border-box" }}
                rows="4" placeholder="Your message..." value={comments}
                onChange={(e) => setComments(e.target.value)} />
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
                <button style={{ background:"#E5E7EB", border:"none", borderRadius:50,
                  padding:"8px 20px", fontWeight:700, cursor:"pointer" }}
                  onClick={() => setShowContact(false)}>Cancel</button>
                <button style={{ background:"linear-gradient(90deg,#7C3AED,#EC4899)", color:"#fff",
                  border:"none", borderRadius:50, padding:"8px 20px", fontWeight:700, cursor:"pointer" }}
                  onClick={handleSendMail}>Send 🚀</button>
              </div>
            </div>
          </div>
        )}

        {/* ── READ ENGLISH MODAL ── */}
        {readModal && (
          <div className="vk-modal-overlay">
            <div className="vk-modal-box">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <h5 style={{ margin:0, color:"#7C3AED", display:"flex", alignItems:"center" }}>
                  📢 Read Content
                  {isSpeaking && <span className="vk-speaking-icon"><span/><span/><span/><span/><span/></span>}
                </h5>
                <button style={{ background:"none", border:"none", fontSize:22, cursor:"pointer" }} onClick={closeReadModal}>✕</button>
              </div>
              <div style={{ fontWeight:700, marginBottom:8, color:"#1e1b4b" }}>{readContent.question}</div>
              <div style={{ color:"#555", lineHeight:1.7 }}>{readContent.answer}</div>
            </div>
          </div>
        )}

        {/* ── READ TAMIL MODAL ── */}
        {tamilModal && (
          <div className="vk-modal-overlay">
            <div className="vk-modal-box" style={{ fontFamily:"'Noto Sans Tamil',sans-serif" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <h5 style={{ margin:0, color:"#F59E0B", display:"flex", alignItems:"center" }}>
                  🔊 தமிழ் வாசிப்பு
                  {isSpeaking && <span className="vk-speaking-icon"><span/><span/><span/><span/><span/></span>}
                </h5>
                <button style={{ background:"none", border:"none", fontSize:22, cursor:"pointer" }} onClick={closeTamilModal}>✕</button>
              </div>
              <div style={{ fontWeight:700, marginBottom:8, color:"#78350f" }}>{tamilContent.question}</div>
              <div style={{ color:"#555", lineHeight:1.7 }}>{tamilContent.answer}</div>
            </div>
          </div>
        )}

        {/* ── PLAY MODAL ── */}
        {playModal && (
          <div className="vk-modal-overlay">
            <div className="vk-modal-box" style={{ maxWidth:680 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <h5 style={{ margin:0, color:"#7C3AED", display:"flex", alignItems:"center" }}>
                  {playLang === "en" ? "📢 Reading in English" : "🔊 தமிழில் வாசிக்கிறோம்"}
                  {isSpeaking && !isPaused && <span className="vk-speaking-icon"><span/><span/><span/><span/><span/></span>}
                </h5>
                <button style={{ background:"none", border:"none", fontSize:22, cursor:"pointer" }} onClick={closePlayModal}>✕</button>
              </div>

              <div ref={modalContentRef} style={{ maxHeight:"60vh", overflowY:"auto", paddingRight:4 }}>
                {playLang === "en" && playItems[playIndex]?.row.slide && (
                  <img src={`/resources/${playItems[playIndex].row.slide}`}
                    style={{ width:"100%", borderRadius:10, marginBottom:12 }} alt="Slide" />
                )}
                {playLang === "ta" && playItems[playIndex]?.row.slide_tamil && (
                  <img src={`/resources/${playItems[playIndex].row.slide_tamil}`}
                    style={{ width:"100%", borderRadius:10, marginBottom:12 }} alt="Tamil Slide" />
                )}

                <div className="vk-q" style={{ fontWeight:700, fontSize:15, color:"#1e1b4b", marginBottom:8 }}>
                  {(playLang === "en" ? playItems[playIndex]?.row.question_eng : playItems[playIndex]?.row.question_tamil)
                    ?.split(/(\s+)/).map((token, ti) => {
                      if (/^\s+$/.test(token)) return token;
                      const wordIdx = (playLang === "en" ? playItems[playIndex]?.row.question_eng : playItems[playIndex]?.row.question_tamil)
                        ?.slice(0, (playLang === "en" ? playItems[playIndex]?.row.question_eng : playItems[playIndex]?.row.question_tamil)?.indexOf(token))
                        .match(/\S+/g)?.length || 0;
                      return <span key={ti} className={`vk-word${highlightPhase==="q" && highlightWord===wordIdx ? " active":""}`}>{token}</span>;
                    })
                  }
                </div>
                <div className="vk-a" style={{ color:"#555", lineHeight:1.9, marginBottom:14 }}>
                  {(playLang === "en" ? playItems[playIndex]?.row.answer_english : playItems[playIndex]?.row.answer_tamil)
                    ?.split(/(\s+)/).map((token, ti, arr) => {
                      if (/^\s+$/.test(token)) return token;
                      const preceding = arr.slice(0, ti).join("");
                      const wordIdx = (preceding.match(/\S+/g) || []).length;
                      return <span key={ti} className={`vk-word${highlightPhase==="a" && highlightWord===wordIdx ? " active":""}`}>{token}</span>;
                    })
                  }
                </div>

                <div className="vk-read-progress" style={{ marginBottom:8 }}>
                  <div className="vk-read-progress-bar" style={{ width:`${progress}%` }} />
                </div>
                <div style={{ textAlign:"center", fontSize:13, color:"#7C3AED", fontWeight:700, marginBottom:10 }}>
                  {playIndex + 1} / {playItems.length}
                </div>
              </div>

              <div className="vk-round-controls">
                <button className="vk-round-btn" disabled={playIndex===0}
                  onClick={() => { speechSynthesis.cancel(); const p=playIndex-1; setPlayIndex(p); setProgress((p/playItems.length)*100); playSpeech(playItems,p,playLang,0); }}>
                  ⏮
                </button>
                {!isPaused
                  ? <button className="vk-round-btn vk-pause" onClick={pauseReading}>⏸</button>
                  : <button className="vk-round-btn vk-resume" onClick={resumeReading}>▶️</button>
                }
                <button className="vk-round-btn vk-stop" onClick={closePlayModal}>⏹</button>
                <button className="vk-round-btn" disabled={playIndex===playItems.length-1}
                  onClick={() => { speechSynthesis.cancel(); const n=playIndex+1; setPlayIndex(n); setProgress((n/playItems.length)*100); playSpeech(playItems,n,playLang,0); }}>
                  ⏭
                </button>
              </div>

            </div>
          </div>
        )}

        {/* ── VOICE DEBUG MODAL ── */}
        {showVoiceDebug && (
          <div className="vk-modal-overlay">
            <div className="vk-modal-box" style={{ maxWidth:600 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <h5 style={{ margin:0, color:"#7C3AED" }}>🔊 Available Voices on this Device</h5>
                <button style={{ background:"none", border:"none", fontSize:22, cursor:"pointer" }}
                  onClick={() => setShowVoiceDebug(false)}>✕</button>
              </div>
              <div style={{ marginBottom:10, padding:"10px 14px", borderRadius:10,
                background: tamilVoice ? "#D1FAE5" : "#FEE2E2",
                color: tamilVoice ? "#065F46" : "#991B1B", fontWeight:700, fontSize:13 }}>
                Tamil Voice: {tamilVoiceLabel}
              </div>
              <div style={{ maxHeight:320, overflowY:"auto", fontSize:12 }}>
                {voicesRef.current.length === 0
                  ? <div style={{ color:"#999", textAlign:"center", padding:20 }}>
                      No voices loaded yet. Try refreshing the page.
                    </div>
                  : voicesRef.current.map((v, i) => (
                    <div key={i} style={{
                      padding:"6px 10px", borderRadius:8, marginBottom:4,
                      background: v.lang.startsWith("ta") ? "#D1FAE5" : "#F5F3FF",
                      color: v.lang.startsWith("ta") ? "#065F46" : "#4C1D95",
                      fontWeight: v.lang.startsWith("ta") ? 700 : 400,
                      display:"flex", justifyContent:"space-between"
                    }}>
                      <span>{v.name}</span>
                      <span style={{ opacity:.7 }}>{v.lang}</span>
                    </div>
                  ))
                }
              </div>
              {!tamilVoice && (
                <div style={{ marginTop:12, padding:"10px 14px", borderRadius:10,
                  background:"#FEF3C7", color:"#92400E", fontSize:12, lineHeight:1.6 }}>
                  ⚠️ <strong>No Tamil voice found.</strong> To enable Tamil TTS:<br/>
                  • <strong>Android:</strong> Settings → General Management → Language → Text-to-speech → Install Tamil<br/>
                  • <strong>Windows:</strong> Settings → Time & Language → Speech → Add voices → Tamil<br/>
                  • <strong>Mac/iOS:</strong> Settings → Accessibility → Spoken Content → Voices → Tamil
                </div>
              )}
              <div style={{ textAlign:"center", marginTop:14 }}>
                <button style={{ background:"linear-gradient(90deg,#7C3AED,#EC4899)", color:"#fff",
                  border:"none", borderRadius:50, padding:"8px 24px", fontWeight:700, cursor:"pointer" }}
                  onClick={() => setShowVoiceDebug(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ── PICK & SHARE FLOATING BAR ── */}
        {pickMode && (
          <div style={{
            position:"fixed", bottom:68, left:0, right:0, zIndex:300,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            background:"linear-gradient(90deg,#1E3A5F,#2563EB)",
            padding:"10px 18px", gap:10,
            boxShadow:"0 -3px 20px rgba(37,99,235,0.4)",
          }}>
            <span style={{ color:"#fff", fontWeight:700, fontSize:13 }}>
              {pickedIds.size === 0
                ? "Tap questions to select (max 10)"
                : `${pickedIds.size}/10 questions selected`}
            </span>
            <div style={{ display:"flex", gap:8 }}>
              <button style={{
                background:"rgba(255,255,255,0.15)", color:"#fff",
                border:"1px solid rgba(255,255,255,0.3)", borderRadius:50,
                padding:"6px 16px", fontWeight:700, fontSize:13, cursor:"pointer"
              }} onClick={exitPickMode}>✕ Cancel</button>
              <button style={{
                background: pickedIds.size > 0 ? "#25D366" : "#555",
                color:"#fff", border:"none", borderRadius:50,
                padding:"6px 18px", fontWeight:700, fontSize:13,
                cursor: pickedIds.size > 0 ? "pointer" : "not-allowed",
                opacity: pickedIds.size > 0 ? 1 : 0.5,
              }}
              disabled={pickedIds.size === 0}
              onClick={handleSharePickWhatsApp}>
                📲 Share via WhatsApp
              </button>
            </div>
          </div>
        )}

        {/* ── BOTTOM NAV BAR ── */}
        <div className="vk-bottom-nav">
          {[
            { id:"home",      icon:"🏠", label:"Home",      action:() => { setActiveNav("home"); window.scrollTo({top:0,behavior:"smooth"}); } },
            { id:"search",    icon:"🔍", label:"Search",    action:() => { setActiveNav("search"); document.querySelector(".vk-search")?.focus(); } },
            { id:"bookmarks", icon:"🔖", label:"Bookmarks", action:() => { setActiveNav("bookmarks"); setShowBookmarks(true); } },
            { id:"export",    icon:"📄", label:"Export PDF", action:() => { setActiveNav("export"); exportBookmarksPDF(); } },
            { id:"settings",  icon:"⚙️", label:"Settings",  action:() => { setActiveNav("settings"); setShowVoiceDebug(true); } },
          ].map(item => (
            <button key={item.id} className={`vk-bnav-item${activeNav===item.id?" active":""}`} onClick={item.action}>
              <span className="vk-bnav-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>

        {/* ── SHARE MODAL ── */}
        {shareModalRow && (() => {
          const lang     = shareModalLang;
          const row      = shareModalRow;
          const slideImg = (lang === "ta" && row.slide_tamil)
            ? `/resources/${row.slide_tamil}`
            : (row.slide ? `/resources/${row.slide}` : null);
          const taFont   = { fontFamily:"'Noto Sans Tamil',sans-serif" };
          return (
            <div className="vk-share-modal-overlay" onClick={() => setShareModalRow(null)}>
              <div className="vk-share-modal-box" onClick={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:"#7C3AED" }}>📤 Share Q&A</div>
                  <button style={{ background:"none", border:"none", fontSize:20, cursor:"pointer" }}
                    onClick={() => setShareModalRow(null)}>✕</button>
                </div>

                {/* ── Language Tabs ── */}
                <div style={{ display:"flex", gap:4, marginBottom:14, background:"#F5F3FF", borderRadius:50, padding:4 }}>
                  {[["en","🇬🇧 English"],["ta","🇮🇳 Tamil"],["both","🌐 Both"]].map(([k,lbl]) => (
                    <button key={k}
                      onClick={() => { setShareModalLang(k); setGeneratedImg(null); setTimeout(() => generateCardImage(), 120); }}
                      style={{ flex:1, border:"none", borderRadius:50, padding:"6px 4px",
                        fontWeight:700, fontSize:12, cursor:"pointer", transition:"all .18s",
                        background: lang===k ? "#7C3AED":"transparent",
                        color:      lang===k ? "#fff":"#7C3AED" }}>
                      {lbl}
                    </button>
                  ))}
                </div>

                {/* ══ OFF-SCREEN BRANDED CARD (html2canvas reads this) ══ */}
                <div ref={shareCardRef} className="vk-gen-card">
                  {/* gradient header */}
                  <div className="vk-gen-header">
                    <span className="vk-gen-header-brand">📚 {WA_SITE_NAME}</span>
                    <span className="vk-gen-header-chapter">{row.chapter}</span>
                  </div>
                  {/* slide image */}
                  {slideImg && (
                    <img className="vk-gen-card-img" src={slideImg}
                      crossOrigin="anonymous" alt="slide" />
                  )}
                  {/* Q&A body */}
                  <div className="vk-gen-card-body">
                    {(lang==="en" || lang==="both") && (
                      <div className="vk-gen-section vk-gen-section-en">
                        <div className="vk-gen-lang-badge" style={{ color:"#7C3AED" }}>🇬🇧 ENGLISH</div>
                        <div className="vk-gen-q">{row.question_eng}</div>
                        <div className="vk-gen-a">{row.answer_english}</div>
                      </div>
                    )}
                    {(lang==="ta" || lang==="both") && (
                      <div className="vk-gen-section vk-gen-section-ta">
                        <div className="vk-gen-lang-badge" style={{ color:"#D97706" }}>🇮🇳 தமிழ்</div>
                        <div className="vk-gen-q" style={{ color:"#92400E", ...taFont }}>{row.question_tamil}</div>
                        <div className="vk-gen-a" style={taFont}>{row.answer_tamil}</div>
                      </div>
                    )}
                  </div>
                  {/* gradient footer */}
                  <div className="vk-gen-footer">
                    <span>🌐 {WA_URL}</span>
                    <span>📚 VK Study — Learn Smart</span>
                  </div>
                </div>

                {/* ══ IMAGE PREVIEW AREA ══ */}
                {imgGenerating && (
                  <div style={{ textAlign:"center", padding:"24px 0", color:"#7C3AED" }}>
                    <div style={{ fontSize:28, marginBottom:8 }}>⏳</div>
                    <div style={{ fontWeight:700, fontSize:13 }}>Generating image card...</div>
                    <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4 }}>This takes 1–2 seconds</div>
                  </div>
                )}

                {!imgGenerating && generatedImg && (
                  <>
                    {/* Live preview */}
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#9CA3AF",
                        textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>
                        Preview
                      </div>
                      <img src={generatedImg} alt="preview" className="vk-canvas-preview" />
                    </div>

                    {/* Download button — primary CTA */}
                    <button onClick={() => downloadImage(generatedImg, row)}
                      style={{ width:"100%", background:"linear-gradient(135deg,#7C3AED,#EC4899)",
                        color:"#fff", border:"none", borderRadius:50, padding:"11px",
                        fontWeight:700, fontSize:14, cursor:"pointer", marginBottom:12,
                        display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                      ⬇️ Save Image to Device
                    </button>

                    {/* Step guide — adapts to mobile vs desktop */}
                    <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0",
                      borderRadius:12, padding:"10px 14px", marginBottom:12, fontSize:12, lineHeight:1.8 }}>
                      {navigator.canShare ? (
                        <>
                          <div style={{ fontWeight:700, color:"#15803D", marginBottom:4 }}>📱 Mobile — one tap to share:</div>
                          <div style={{ color:"#166534" }}>
                            Tap <strong>Facebook</strong> or <strong>Instagram</strong> below →
                            your phone's share sheet opens →
                            select the app → image attaches automatically ✅
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontWeight:700, color:"#15803D", marginBottom:4 }}>🖥️ Desktop — 2 steps:</div>
                          <div style={{ color:"#166534" }}>
                            1️⃣ Click <strong>⬇️ Save Image</strong> above<br/>
                            2️⃣ Click <strong>Facebook</strong> or <strong>Instagram</strong> → attach the saved image to your post
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {!imgGenerating && !generatedImg && (
                  <div style={{ textAlign:"center", padding:"16px 0 12px", color:"#9CA3AF", fontSize:12 }}>
                    Preparing image...
                  </div>
                )}

                {/* ══ PLATFORM BUTTONS ══ */}
                <div style={{ fontSize:11, fontWeight:700, color:"#9CA3AF",
                  textTransform:"uppercase", letterSpacing:1, marginBottom:7 }}>
                  Share via
                </div>
                <div className="vk-share-actions">

                  {/* WhatsApp — text share, works on all devices */}
                  <button className="vk-share-action-btn" style={{ background:"#25D366", color:"#fff" }}
                    onClick={() => shareWA(row, lang)}>
                    📲 WhatsApp
                  </button>

                  {/* Facebook */}
                  <button className="vk-share-action-btn" style={{ background:"#1877F2", color:"#fff" }}
                    disabled={!generatedImg}
                    onClick={() => shareImageNative(generatedImg, row, "facebook")}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
                    Facebook
                  </button>

                  {/* Instagram */}
                  <button className="vk-share-action-btn"
                    style={{ background:"linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)", color:"#fff" }}
                    disabled={!generatedImg}
                    onClick={() => shareImageNative(generatedImg, row, "instagram")}>
                    📸 Instagram
                  </button>

                </div>

                {/* Contextual tip based on device capability */}
                <div style={{ textAlign:"center", fontSize:11, color:"#9CA3AF", marginTop:8, lineHeight:1.6 }}>
                  {navigator.canShare
                    ? "📱 On mobile: tapping Facebook or Instagram opens your phone's share sheet — select the app to post directly with the image"
                    : "🖥️ On desktop: image saves to your device — then attach it when creating your Facebook or Instagram post"}
                </div>

              </div>
            </div>
          );
        })()}

        {/* ── MESSENGER CHAT BUBBLE ── */}
        <button className="vk-messenger-bubble" title="Chat on Messenger"
          onClick={() => setShowMessenger(!showMessenger)}>
          {/* Messenger logo SVG */}
          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 4C13 4 4 12.5 4 23c0 5.8 2.7 11 7 14.6V44l6.4-3.5A21.2 21.2 0 0 0 24 42c11 0 20-8.5 20-19S35 4 24 4z" fill="white"/>
            <path d="M26.2 29.7l-5.1-5.4-10 5.4 10.9-11.6 5.2 5.4 9.9-5.4-10.9 11.6z" fill="#0084FF"/>
          </svg>
        </button>

        {/* ── MESSENGER PANEL ── */}
        {showMessenger && (
          <div className="vk-messenger-panel">
            <div className="vk-messenger-header">
              {/* Replace with your page icon URL */}
              <div style={{ width:32, height:32, borderRadius:"50%", background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, border:"2px solid #fff" }}>📚</div>
              <div>
                <div>VK Study</div>
                <div style={{ fontSize:11, fontWeight:400, opacity:.85 }}>Typically replies instantly</div>
              </div>
              <button className="vk-messenger-header-close" onClick={() => setShowMessenger(false)}>✕</button>
            </div>
            <div className="vk-messenger-body">
              <div className="vk-msg-bubble">
                👋 Hi! Welcome to VK Study.<br/>How can we help you today?
              </div>
            </div>
            <button className="vk-messenger-open-btn" onClick={openMessenger}>
              💬 Continue in Messenger
            </button>
            <div style={{ textAlign:"center", fontSize:10, color:"#9CA3AF", paddingBottom:10 }}>
              Powered by Facebook Messenger
            </div>
          </div>
        )}

        {/* ── TOAST ── */}
        {toast && <div className="vk-toast">{toast}</div>}

      </div>
    </>
  );
}